import { Button, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { useAccess } from '../../auth/useAccess.js';

import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import { EmployeeStatusBadge } from './components/EmployeeStatusBadge.js';
import type { EmployeeDetailDto } from './contractsMirror.js';
import { PhoneSignInCode } from './PhoneSignInCode.js';
import { SignedInDevices } from './SignedInDevices.js';

/**
 * The redesigned employee profile detail (UI-SPEC archetype A detail — "employee
 * profile detail"). Detail header (`<h1>` = employee name + status badge + action row)
 * + stacked section cards: Pay & Eligibility (EVERY pay field — D-03 load-bearing) and
 * Contact. Sensitive pay numbers render in `font-mono` at AA contrast; every color is a
 * locked token (BRAND-06). The pay fields feed payroll + profitability — all are shown.
 */

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wide">{label}</dt>
      <dd className="text-foreground text-sm">{children}</dd>
    </div>
  );
}

function YesNo({ value }: { value: number }) {
  const { t } = useTranslation();
  return <>{value ? t('employees:common.yes') : t('employees:common.no')}</>;
}

export function EmployeeDetailView({ employee }: { employee: EmployeeDetailDto }) {
  const { t } = useTranslation();
  const fmt = useMoneyFormatters();
  const canEdit = useAccess()({ permission: 'employees.edit' });

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display font-semibold text-3xl text-foreground">{employee.name}</h1>
          <EmployeeStatusBadge employee={employee} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit ? (
            <Button asChild>
              <Link to="/employees/$id/edit" params={{ id: String(employee.id) }}>
                {t('employees:actions.edit')}
              </Link>
            </Button>
          ) : null}
          <Button asChild variant="secondary">
            <Link to="/employees">{t('employees:actions.back')}</Link>
          </Button>
        </div>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
            {t('employees:detail.payHeading')}
          </h2>
          <dl className="grid grid-cols-2 gap-4">
            <Field label={t('employees:form.payType')}>
              {t(`employees:payType.${String(employee.payType).toLowerCase()}`, {
                defaultValue: employee.payType,
              })}
            </Field>
            <Field label={t('employees:form.hourlyRate')}>
              <span className="font-mono">
                {employee.hourlyRate ? fmt.formatCurrency(employee.hourlyRate) : '—'}
              </span>
            </Field>
            <Field label={t('employees:form.weeklySalary')}>
              <span className="font-mono">
                {employee.weeklySalary ? fmt.formatCurrency(employee.weeklySalary) : '—'}
              </span>
            </Field>
            <Field label={t('employees:form.annualSalary')}>
              <span className="font-mono">
                {employee.annualSalary ? fmt.formatCurrency(employee.annualSalary) : '—'}
              </span>
            </Field>
            <Field label={t('employees:form.overtimeEligible')}>
              <YesNo value={employee.overtimeEligible} />
            </Field>
            <Field label={t('employees:form.lunchDeductionExempt')}>
              <YesNo value={employee.lunchDeductionExempt} />
            </Field>
          </dl>
        </section>

        <section className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
            {t('employees:detail.contactHeading')}
          </h2>
          <dl className="grid grid-cols-1 gap-4">
            <Field label={t('employees:form.email')}>
              {employee.email || (
                <span className="text-muted-foreground">{t('employees:detail.noEmail')}</span>
              )}
            </Field>
            <Field label={t('employees:form.phone')}>
              {employee.phone ? (
                <span className="font-mono">{employee.phone}</span>
              ) : (
                <span className="text-muted-foreground">{t('employees:detail.noPhone')}</span>
              )}
            </Field>
          </dl>
        </section>

        {employee.archivedAt ? null : <PhoneSignInCode employeeId={employee.id} />}
        {employee.archivedAt ? null : <SignedInDevices employeeId={employee.id} />}
      </div>
    </div>
  );
}
